import { useEffect, useMemo, useState } from "react";
import { GameShell, useLiveMode } from "@/components/GameShell";
import { FinishScreen } from "@/components/FinishScreen";
import { WORDLE_PUZZLES, dailySeed, type WordlePuzzle } from "@/data/quotes";
import {
  WORDLE_MAX_GUESSES,
  loadBest,
  saveBest,
  buildShareText,
} from "@/lib/game-utils";

export type WordleMode = "daily" | "endless";

type Feedback = "hit" | "near" | "miss";

interface GuessRow {
  letters: string[];
  feedback: Feedback[];
}

function scoreFeedback(guess: string, target: string): Feedback[] {
  const g = guess.toLowerCase().split("");
  const t = target.toLowerCase().split("");
  const fb: Feedback[] = g.map(() => "miss");
  const counts: Record<string, number> = {};
  // First pass: exact hits
  g.forEach((ch, i) => {
    if (ch === t[i]) fb[i] = "hit";
    else counts[t[i]] = (counts[t[i]] ?? 0) + 1;
  });
  // Second pass: present-but-wrong-place
  g.forEach((ch, i) => {
    if (fb[i] === "hit") return;
    if ((counts[ch] ?? 0) > 0) {
      fb[i] = "near";
      counts[ch] -= 1;
    }
  });
  return fb;
}

function gridEmoji(rows: GuessRow[]): string {
  return rows.map((r) => r.feedback.map((f) => f === "hit" ? "🟩" : f === "near" ? "🟨" : "⬜").join("")).join("\n");
}

function pickPuzzleForMode(mode: WordleMode, exclude: Set<string>): WordlePuzzle {
  if (mode === "daily") {
    const seed = dailySeed();
    const x = (seed * 9301 + 49297) % 233280;
    const idx = Math.floor((x / 233280) * WORDLE_PUZZLES.length);
    return WORDLE_PUZZLES[Math.max(0, Math.min(WORDLE_PUZZLES.length - 1, idx))];
  }
  // Endless: never repeat until the whole 100+ puzzle library is exhausted,
  // then quietly recycle so a session can run indefinitely.
  const pool = WORDLE_PUZZLES.filter((p) => !exclude.has(p.id));
  const src = pool.length ? pool : WORDLE_PUZZLES;
  return src[Math.floor(Math.random() * src.length)];
}

function roundScore(guessesUsed: number, solved: boolean): number {
  if (!solved) return 0;
  return Math.max(1, WORDLE_MAX_GUESSES + 1 - guessesUsed);
}

const HINT_LABELS = ["Theme", "Era", "Author", "First letter"];

export function QuoteWordle({ mode = "endless" }: { mode?: WordleMode }) {
  const live = useLiveMode();
  const isDaily = mode === "daily";
  // Endless has no round cap — you keep going until you choose to cash out.
  const totalRounds = isDaily ? 1 : Infinity;
  const storageKey = isDaily ? "quote-wordle-daily" : "quote-wordle";

  // round state
  const [roundIdx, setRoundIdx] = useState(0);
  const [usedIds, setUsedIds] = useState<Set<string>>(() => new Set());
  const [puzzle, setPuzzle] = useState<WordlePuzzle>(() => pickPuzzleForMode(mode, new Set()));
  const [guesses, setGuesses] = useState<GuessRow[]>([]);
  const [current, setCurrent] = useState("");
  const [done, setDone] = useState<"won" | "lost" | null>(null);

  // session
  const [score, setScore] = useState(0);
  const [allGrids, setAllGrids] = useState<string[]>([]);
  const [finished, setFinished] = useState(false);
  const [best, setBest] = useState(0);

  useEffect(() => { setBest(loadBest(storageKey)); }, [storageKey]);

  // Reset on mode change.
  useEffect(() => {
    const first = pickPuzzleForMode(mode, new Set());
    setUsedIds(new Set([first.id]));
    setPuzzle(first);
    setGuesses([]);
    setCurrent("");
    setDone(null);
    setScore(0);
    setAllGrids([]);
    setFinished(false);
    setRoundIdx(0);
  }, [mode]);

  const len = puzzle.word.length;
  const wrongCount = guesses.filter((g) => g.feedback.some((f) => f !== "hit") || g.letters.join("") !== puzzle.word).length;
  const hintsUnlocked = Math.min(wrongCount, HINT_LABELS.length);

  // Per-letter best feedback across all guesses (for keyboard coloring).
  const keyState = useMemo(() => {
    const rank: Record<Feedback, number> = { miss: 1, near: 2, hit: 3 };
    const map: Record<string, Feedback> = {};
    for (const row of guesses) {
      row.letters.forEach((ch, i) => {
        const f = row.feedback[i];
        if (!map[ch] || rank[f] > rank[map[ch]]) map[ch] = f;
      });
    }
    return map;
  }, [guesses]);

  const submitGuess = () => {
    if (done) return;
    if (current.length !== len) return;
    const fb = scoreFeedback(current, puzzle.word);
    const row: GuessRow = { letters: current.toLowerCase().split(""), feedback: fb };
    const next = [...guesses, row];
    setGuesses(next);
    setCurrent("");
    const solved = row.letters.join("") === puzzle.word;
    if (solved) setDone("won");
    else if (next.length >= WORDLE_MAX_GUESSES) setDone("lost");
  };

  const pressKey = (key: string) => {
    if (finished || done) return;
    if (key === "ENTER") { submitGuess(); return; }
    if (key === "BACK") { setCurrent((c) => c.slice(0, -1)); return; }
    if (/^[a-z]$/i.test(key) && current.length < len) {
      setCurrent((c) => (c + key.toLowerCase()).slice(0, len));
    }
  };

  // Physical keyboard input
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (finished || done) return;
      if (e.key === "Enter") { submitGuess(); return; }
      if (e.key === "Backspace") { setCurrent((c) => c.slice(0, -1)); return; }
      if (/^[a-zA-Z]$/.test(e.key) && current.length < len) {
        setCurrent((c) => (c + e.key.toLowerCase()).slice(0, len));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const nextRound = () => {
    const earned = roundScore(guesses.length, done === "won");
    const grid = gridEmoji(guesses);
    setScore((s) => s + earned);
    setAllGrids((g) => [...g, `${roundIdx + 1}. ${done === "won" ? guesses.length : "X"}/${WORDLE_MAX_GUESSES}\n${grid}`]);

    if (roundIdx + 1 >= totalRounds) {
      const final = score + earned;
      saveBest(storageKey, final);
      setBest((b) => Math.max(b, final));
      setFinished(true);
      return;
    }
    const newUsed = new Set(usedIds);
    const np = pickPuzzleForMode(mode, newUsed);
    newUsed.add(np.id);
    setUsedIds(newUsed);
    setPuzzle(np);
    setGuesses([]);
    setCurrent("");
    setDone(null);
    setRoundIdx((i) => i + 1);
  };

  // Endless: end the run on demand and go to the share/results screen.
  const cashOut = () => {
    saveBest(storageKey, score);
    setBest((b) => Math.max(b, score));
    setFinished(true);
  };

  const restart = () => {
    const first = pickPuzzleForMode(mode, new Set());
    setUsedIds(new Set([first.id]));
    setPuzzle(first);
    setGuesses([]);
    setCurrent("");
    setDone(null);
    setScore(0);
    setAllGrids([]);
    setFinished(false);
    setRoundIdx(0);
  };

  // Render template with the missing word as styled blank or revealed first letter
  const renderedQuote = useMemo(() => {
    const reveal = hintsUnlocked >= 4 ? puzzle.word[0].toUpperCase() + "·".repeat(Math.max(0, puzzle.word.length - 1)) : "·".repeat(puzzle.word.length);
    return puzzle.template.replace("___", reveal);
  }, [puzzle, hintsUnlocked]);

  if (finished) {
    const roundsPlayed = allGrids.length || 1;
    const maxScore = roundsPlayed * WORDLE_MAX_GUESSES;
    const gameUrl =
      typeof window !== "undefined"
        ? `${window.location.origin}/games/quote-wordle`
        : `https://mrbquotes.com/games/quote-wordle`;
    const solvedCount = allGrids.filter((g) => !g.includes("X/")).length;
    const shareText = buildShareText({
      gameName: isDaily ? `Quote Wordle · Daily` : `Quote Wordle`,
      scoreLine: isDaily
        ? `I cracked today's quote — ${score} / ${maxScore}. Can you beat me?`
        : `I solved ${solvedCount}/${roundsPlayed} famous quotes in one endless run — ${score} points. Your turn:`,
      emojiGrid: allGrids.join("\n\n"),
      url: gameUrl,
      extra: "🎯 Free · No signup · New quote every day",
    });
    return (
      <FinishScreen
        score={score}
        total={maxScore}
        best={best}
        onRestart={restart}
        title={isDaily ? "Quote Wordle · Daily" : "Quote Wordle"}
        numeral="VI"
        hubSlug="quote-wordle"
        shareText={shareText}
        emojiGrid={allGrids.join("\n\n")}
      />
    );
  }

  const totalLabel = isDaily ? 1 : undefined;
  const guessGrid: Array<GuessRow | null> = Array.from({ length: WORDLE_MAX_GUESSES }, (_, i) =>
    guesses[i] ?? null
  );

  const KB_ROWS = ["qwertyuiop", "asdfghjkl", "zxcvbnm"];

  return (
    <GameShell
      title={isDaily ? "Quote Wordle · Daily" : "Quote Wordle"}
      numeral="VI"
      score={score}
      round={roundIdx + 1}
      total={totalLabel}
      hubSlug="quote-wordle"
    >
      {/* Compact quote header — kept short so the board is above the fold on mobile */}
      <article className="rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-elegant grain animate-reveal">
        <div className="flex items-baseline justify-between gap-3">
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">The quote</div>
          <div className="text-[10px] text-muted-foreground">
            <span className="text-gold font-mono">{puzzle.word.length}</span> letters · <span className="text-gold font-mono">{WORDLE_MAX_GUESSES - guesses.length}</span> left
          </div>
        </div>
        <p className={`mt-2 font-display leading-snug ${live ? "text-2xl sm:text-4xl" : "text-base sm:text-xl"}`}>
          {renderedQuote}
        </p>
      </article>

      {/* Guess grid — primary focus, sits right under the quote on mobile */}
      <div className="mt-4 flex flex-col items-center gap-1.5">
        {guessGrid.map((row, rIdx) => {
          const isCurrent = !done && rIdx === guesses.length;
          const letters: Array<{ ch: string; fb?: Feedback }> = row
            ? row.letters.map((ch, i) => ({ ch, fb: row.feedback[i] }))
            : Array.from({ length: len }, (_, i) => ({ ch: isCurrent ? (current[i] ?? "") : "" }));
          return (
            <div key={rIdx} className="flex gap-1.5">
              {letters.map((cell, i) => {
                const cls =
                  cell.fb === "hit" ? "bg-success text-ink border-success" :
                  cell.fb === "near" ? "bg-gold/80 text-ink border-gold" :
                  cell.fb === "miss" ? "bg-secondary text-muted-foreground border-border" :
                  cell.ch ? "border-gold/60 bg-card text-foreground" :
                  "border-border bg-card/40 text-foreground";
                return (
                  <div key={i}
                    className={`flex items-center justify-center rounded-md border font-display uppercase ${live ? "h-14 w-14 text-2xl sm:h-20 sm:w-20 sm:text-4xl" : "h-10 w-10 text-lg sm:h-14 sm:w-14 sm:text-2xl"} ${cls}`}>
                    {cell.ch}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* On-screen keyboard — works on mobile, where there's no physical keyboard */}
      {!done && (
        <div className="mt-4 flex flex-col items-center gap-1.5 select-none">
          {KB_ROWS.map((row, rIdx) => (
            <div key={rIdx} className="flex w-full max-w-md justify-center gap-1">
              {rIdx === 2 && (
                <button
                  type="button"
                  onClick={() => pressKey("ENTER")}
                  disabled={current.length !== len}
                  className="flex-1 max-w-[64px] rounded-md border border-gold/50 bg-gold/10 px-1 py-3 text-[11px] font-semibold uppercase text-gold disabled:opacity-30"
                >Enter</button>
              )}
              {row.split("").map((ch) => {
                const fb = keyState[ch];
                const cls =
                  fb === "hit" ? "bg-success text-ink border-success" :
                  fb === "near" ? "bg-gold/80 text-ink border-gold" :
                  fb === "miss" ? "bg-secondary/60 text-muted-foreground border-border opacity-60" :
                  "bg-card text-foreground border-border";
                return (
                  <button
                    key={ch}
                    type="button"
                    onClick={() => pressKey(ch)}
                    className={`flex-1 max-w-[40px] rounded-md border px-1 py-3 text-sm font-semibold uppercase ${cls}`}
                  >{ch}</button>
                );
              })}
              {rIdx === 2 && (
                <button
                  type="button"
                  onClick={() => pressKey("BACK")}
                  className="flex-1 max-w-[64px] rounded-md border border-border bg-card px-1 py-3 text-[11px] font-semibold uppercase"
                  aria-label="Delete"
                >⌫</button>
              )}
            </div>
          ))}
          <div className="mt-1 text-[10px] text-muted-foreground">Tap keys or use your keyboard · Enter to submit</div>
        </div>
      )}

      {/* Hints — pushed below the game so the board is above the fold */}
      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Hints unlock as you guess</div>
        <div className="grid gap-2 sm:grid-cols-4">
          {HINT_LABELS.map((label, i) => {
            const unlocked = i < hintsUnlocked || done !== null;
            const value =
              i === 0 ? puzzle.theme :
              i === 1 ? (puzzle.era ?? "—") :
              i === 2 ? puzzle.author :
              puzzle.word[0].toUpperCase();
            return (
              <div key={label}
                className={`rounded-lg border px-3 py-2 text-xs transition ${
                  unlocked ? "border-gold/40 bg-gold/5" : "border-border bg-card/60 text-muted-foreground"
                }`}>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Hint {i + 1} · {label}</div>
                <div className={`font-display ${unlocked ? "text-gold" : "opacity-40"}`}>
                  {unlocked ? value : "—"}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {done && (
        <div className="mt-6 rounded-xl border border-border bg-card p-5 text-center">
          {done === "won" ? (
            <div className="text-success font-display text-xl">✓ Solved in {guesses.length} {guesses.length === 1 ? "try" : "tries"}.</div>
          ) : (
            <div className="text-error font-display text-xl">✗ The word was <span className="text-gold uppercase">{puzzle.word}</span></div>
          )}
          <div className="mt-2 font-display text-lg">{puzzle.template.replace("___", puzzle.word)}</div>
          <div className="mt-1 text-sm text-muted-foreground">— {puzzle.author}{puzzle.era ? ` · ${puzzle.era}` : ""}{puzzle.source ? ` · ${puzzle.source}` : ""}</div>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={nextRound}
              className="rounded-full bg-gradient-gold px-6 py-2 text-sm font-semibold text-ink"
            >
              {roundIdx + 1 >= totalRounds ? "See results" : "Next quote →"}
            </button>
            {!isDaily && (
              <button
                onClick={cashOut}
                className="rounded-full border border-gold/50 px-6 py-2 text-sm font-semibold text-gold"
              >
                Finish &amp; share run
              </button>
            )}
          </div>
          {!isDaily && (
            <div className="mt-3 text-[11px] text-muted-foreground">
              Endless mode — keep going as long as you like. {WORDLE_PUZZLES.length} quotes in the library.
            </div>
          )}
        </div>
      )}
    </GameShell>
  );
}
